import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AuthModal } from '@/components/auth/AuthModal'
import { SettingsModal } from '@/components/settings/SettingsModal'
import { SupportChatPanel } from '@/components/support/SupportChatPanel'
import type { AuthMode } from '@/components/auth/AuthForm'
import { useAuth } from '@/contexts/auth-context'

interface AppModalsContextType {
  openAuth: (mode?: AuthMode) => void
  closeAuth: () => void
  openSettings: (section?: string) => void
  closeSettings: () => void
  openSupport: () => void
  closeSupport: () => void
}

const SETTINGS_HASH = '#settings'
const SIGNIN_HASH = '#signin'

const isSettingsHash = (hash: string) => hash === SETTINGS_HASH || hash.startsWith(`${SETTINGS_HASH}/`)

const AppModalsContext = createContext<AppModalsContextType | undefined>(undefined)

export function AppModalsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const isAnon = !user || !!user.isAnonymous
  const location = useLocation()
  const navigate = useNavigate()

  const [authOpen, setAuthOpen] = useState(false)
  const [authMode, setAuthMode] = useState<AuthMode>('signup')
  const [supportOpen, setSupportOpen] = useState(false)

  const settingsOpen = isSettingsHash(location.hash) && !isAnon

  const openAuth = useCallback((mode: AuthMode = 'signup') => {
    setAuthMode(mode)
    setAuthOpen(true)
  }, [])

  const closeAuth = useCallback(() => setAuthOpen(false), [])

  const openSettings = useCallback((section?: string) => {
    if (isAnon) {
      setAuthMode('login')
      setAuthOpen(true)
      return
    }
    const hash = section ? `${SETTINGS_HASH}/${section}` : SETTINGS_HASH
    navigate({ pathname: location.pathname, search: location.search, hash })
  }, [isAnon, navigate, location.pathname, location.search])

  const closeSettings = useCallback(() => {
    navigate(
      { pathname: location.pathname, search: location.search, hash: '' },
      { replace: true },
    )
  }, [navigate, location.pathname, location.search])

  const openSupport = useCallback(() => setSupportOpen(true), [])
  const closeSupport = useCallback(() => setSupportOpen(false), [])

  // Anon visitor with #settings → strip hash, pop auth.
  useEffect(() => {
    if (isSettingsHash(location.hash) && isAnon) {
      navigate({ pathname: location.pathname, search: location.search, hash: '' }, { replace: true })
      setAuthMode('login')
      setAuthOpen(true)
    }
  }, [location.hash, isAnon, navigate, location.pathname, location.search])

  // #signin is the single deep-link hash to pop the auth modal. The modal lets
  // the user toggle between login and signup internally, so one hash is enough.
  // We strip the hash on entry so refreshes don't re-pop the modal.
  useEffect(() => {
    if (location.hash !== SIGNIN_HASH) return
    if (!isAnon) {
      navigate({ pathname: location.pathname, search: location.search, hash: '' }, { replace: true })
      return
    }
    setAuthMode('login')
    setAuthOpen(true)
    navigate({ pathname: location.pathname, search: location.search, hash: '' }, { replace: true })
  }, [location.hash, isAnon, navigate, location.pathname, location.search])

  useEffect(() => {
    if (authOpen && user && !user.isAnonymous) {
      setAuthOpen(false)
    }
  }, [authOpen, user])

  return (
    <AppModalsContext.Provider value={{ openAuth, closeAuth, openSettings, closeSettings, openSupport, closeSupport }}>
      {children}
      <AuthModal open={authOpen} mode={authMode} onOpenChange={setAuthOpen} onModeChange={setAuthMode} />
      <SettingsModal open={settingsOpen} onOpenChange={(open) => (open ? openSettings() : closeSettings())} />
      <SupportChatPanel open={supportOpen} onClose={closeSupport} />
    </AppModalsContext.Provider>
  )
}

export function useAppModals() {
  const ctx = useContext(AppModalsContext)
  if (!ctx) {
    throw new Error('useAppModals must be used inside AppModalsProvider')
  }
  return ctx
}
